import type { CrawlJobApi } from '@liks/backend-svc-api/esm/apis/CrawlJobApi';
import type { CrawlScheduleApi } from '@liks/backend-svc-api/esm/apis/CrawlScheduleApi';
import type {
  CrawlJob,
  CrawlJobStatusDTO,
  CrawlUrlNode,
} from '@liks/backend-svc-api/esm/models/index';
import type { WithResourceId } from '../types/knowledge';
import {
  feedCrawlJobStatusStreamBuffer,
  normalizeCrawlJobStatusResponse,
  parseCrawlJobStatusPayload,
} from './crawlJobStatusStream';
import { deleteCrawlSchedule } from './knowledgeCrawlScheduleOps';
import { hasId } from './knowledgeServiceGuards';

export async function listCrawls(
  crawlApi: CrawlJobApi,
  databaseId: string,
): Promise<Array<WithResourceId<CrawlJob>>> {
  const crawls =
    await crawlApi.deLiksKnowledgebaseBoundaryCrawlJobResourceListCrawlJobs({
      knowledgeBaseId: databaseId,
      limit: 1000,
    });
  return crawls.filter(hasId);
}

export async function startCrawl(
  crawlApi: CrawlJobApi,
  databaseId: string,
  startUrl: string,
  options?: {
    maxDepth?: number | null;
    maxPages?: number | null;
    useStartUrlAsPrefixFilter?: boolean | null;
    allowSubdomains?: boolean | null;
    excludedUrls?: string[] | null;
  },
): Promise<WithResourceId<CrawlJob>> {
  const crawl =
    await crawlApi.deLiksKnowledgebaseBoundaryCrawlJobResourceStartCrawl({
      knowledgeBaseId: databaseId,
      crawlJob: {
        startUrl,
        maxDepth: options?.maxDepth ?? undefined,
        maxPages: options?.maxPages ?? undefined,
        useStartUrlAsPrefixFilter:
          options?.useStartUrlAsPrefixFilter ?? undefined,
        allowSubdomains: options?.allowSubdomains ?? undefined,
        excludedUrls: options?.excludedUrls ?? undefined,
      },
    });

  if (!hasId(crawl)) {
    throw new Error('Backend returned a crawl job without id');
  }

  return crawl;
}

export async function listCrawlUrlNodes(
  crawlApi: CrawlJobApi,
  databaseId: string,
  crawlId: string,
): Promise<Array<WithResourceId<CrawlUrlNode>>> {
  const pageSize = 1000;
  const nodes: Array<WithResourceId<CrawlUrlNode>> = [];
  let offset = 0;

  for (;;) {
    const page =
      await crawlApi.deLiksKnowledgebaseBoundaryCrawlUrlNodeResourceListCrawlUrlNodes(
        {
          knowledgeBaseId: databaseId,
          crawlJobId: crawlId,
          limit: pageSize,
          offset,
        },
      );

    nodes.push(...page.filter(hasId));

    if (page.length < pageSize) {
      break;
    }

    offset += pageSize;
  }

  return nodes;
}

export async function updateCrawlSelection(
  crawlApi: CrawlJobApi,
  databaseId: string,
  crawlId: string,
  selectedNodeIds: string[],
): Promise<void> {
  await crawlApi.deLiksKnowledgebaseBoundaryCrawlUrlNodeResourceUpdateCrawlUrlSelection(
    {
      knowledgeBaseId: databaseId,
      crawlJobId: crawlId,
      crawlUrlSelectionUpdate: { selectedNodeIds },
    },
  );
}

export async function startSelectedUrlCrawl(
  crawlApi: CrawlJobApi,
  databaseId: string,
  crawlId: string,
): Promise<WithResourceId<CrawlJob>> {
  const crawl =
    await crawlApi.deLiksKnowledgebaseBoundaryCrawlJobResourceStartSelectedUrlCrawl(
      { knowledgeBaseId: databaseId, id: crawlId },
    );

  if (!hasId(crawl)) {
    throw new Error('Backend returned a crawl job without id');
  }

  return crawl;
}

export async function retryFailedCrawl(
  crawlApi: CrawlJobApi,
  crawlScheduleApi: CrawlScheduleApi,
  databaseId: string,
  crawl: WithResourceId<CrawlJob>,
  options?: { scheduleId?: string },
): Promise<WithResourceId<CrawlJob>> {
  const startUrl = crawl.startUrl?.trim();
  if (!startUrl) {
    throw new Error('Crawl job has no start URL');
  }

  if (options?.scheduleId) {
    await deleteCrawlSchedule(crawlScheduleApi, databaseId, options.scheduleId);
  }

  await deleteCrawl(crawlApi, databaseId, crawl.id);
  return startCrawl(crawlApi, databaseId, startUrl, {
    maxDepth: crawl.maxDepth,
    maxPages: crawl.maxPages,
    useStartUrlAsPrefixFilter: crawl.useStartUrlAsPrefixFilter,
    allowSubdomains: crawl.allowSubdomains,
    excludedUrls: crawl.excludedUrls,
  });
}

export async function restartCrawl(
  crawlApi: CrawlJobApi,
  databaseId: string,
  crawlId: string,
): Promise<WithResourceId<CrawlJob>> {
  const crawl =
    await crawlApi.deLiksKnowledgebaseBoundaryCrawlJobResourceRestartCrawl({
      knowledgeBaseId: databaseId,
      id: crawlId,
    });

  if (!hasId(crawl)) {
    throw new Error('Backend returned a crawl job without id');
  }

  return crawl;
}

export async function resumeAbandonedCrawl(
  crawlApi: CrawlJobApi,
  databaseId: string,
  crawlId: string,
): Promise<WithResourceId<CrawlJob>> {
  const crawl =
    await crawlApi.deLiksKnowledgebaseBoundaryCrawlJobResourceResumeAbandonedCrawl(
      { knowledgeBaseId: databaseId, id: crawlId },
    );

  if (!hasId(crawl)) {
    throw new Error('Backend returned a crawl job without id');
  }

  return crawl;
}

export async function deleteCrawl(
  crawlApi: CrawlJobApi,
  databaseId: string,
  crawlId: string,
): Promise<void> {
  await crawlApi.deLiksKnowledgebaseBoundaryCrawlJobResourceDeleteCrawlJob({
    knowledgeBaseId: databaseId,
    id: crawlId,
  });
}

export async function getCrawlJobStatus(
  crawlApi: CrawlJobApi,
  crawlId: string,
): Promise<CrawlJobStatusDTO | null> {
  const response =
    await crawlApi.deLiksKnowledgebaseBoundaryCrawlJobStatusResourceReadCrawlJobStatus(
      { id: crawlId },
    );
  return normalizeCrawlJobStatusResponse(response);
}

export async function streamCrawlJobStatus(
  crawlApi: CrawlJobApi,
  crawlId: string,
  handlers: {
    onUpdate: (status: CrawlJobStatusDTO) => void;
    onComplete?: () => void;
  },
  signal?: AbortSignal,
): Promise<void> {
  const response =
    await crawlApi.deLiksKnowledgebaseBoundaryCrawlJobStatusResourceStreamCrawlJobStatusRaw(
      { id: crawlId },
      async ({ init }) => ({
        ...init,
        headers: {
          ...init.headers,
          Accept: 'text/event-stream, application/x-ndjson, application/json',
        },
      }),
    );

  if (signal?.aborted) {
    return;
  }

  const body = response.raw.body;
  if (!body) {
    const fallback = await response.value();
    const normalized = normalizeCrawlJobStatusResponse(fallback);
    if (normalized) {
      handlers.onUpdate(normalized);
      if (normalized.status === 'COMPLETED' || normalized.status === 'FAILED') {
        handlers.onComplete?.();
      }
    }
    return;
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let pendingBuffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    pendingBuffer += decoder.decode(value, { stream: true });
    pendingBuffer = feedCrawlJobStatusStreamBuffer(pendingBuffer, handlers);

    if (signal?.aborted) {
      await reader.cancel();
      return;
    }
  }

  pendingBuffer += decoder.decode();
  pendingBuffer = feedCrawlJobStatusStreamBuffer(pendingBuffer, handlers);

  const trailing = parseCrawlJobStatusPayload(pendingBuffer);
  if (trailing) {
    handlers.onUpdate(trailing);
    if (trailing.status === 'COMPLETED' || trailing.status === 'FAILED') {
      handlers.onComplete?.();
    }
  }
}
